import { useEffect, useRef, useState } from "react";

function AnimatedSection({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {children}
    </div>
  );
}

export default function Contact() {
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("sending");
    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch("https://readdy.ai/api/form/d80t8g6jseoa69cggfr0", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus("sent");
        form.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <div className="min-h-screen pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[260px] md:h-[320px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20welcoming%20Malaysian%20office%20interior%20with%20green%20plants%20natural%20wood%20furniture%20soft%20warm%20lighting%20earth%20tone%20palette%20clean%20minimal%20design%20friendly%20atmosphere%20community%20center%20vibe&width=1600&height=500&seq=conthero&orientation=landscape"
          alt="Pejabat Mulia Insan"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-teal-900/70" />
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white">
            Hubungi Kami
          </h1>
          <p className="text-white/80 text-sm md:text-base mt-3 max-w-xl">
            Kami sedia mendengar anda. Hubungi kami untuk sebarang pertanyaan,
            sumbangan, atau kerjasama.
          </p>
        </div>
      </section>

      {/* Contact content */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Contact info */}
            <div>
              <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
                Maklumat Hubungan
              </p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Berhubung Dengan Kami
              </h2>

              <div className="space-y-5 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-50 shrink-0">
                    <i className="ri-map-pin-line text-teal-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      Alamat Pejabat
                    </div>
                    <p className="text-xs md:text-sm text-gray-500 mt-0.5 leading-relaxed">
                      No. 23, Jalan Mulia 4/1,
                      <br />
                      Taman Mulia Indah,
                      <br />
                      43000 Kajang, Selangor
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-50 shrink-0">
                    <i className="ri-phone-line text-teal-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      Telefon
                    </div>
                    <p className="text-xs md:text-sm text-gray-500 mt-0.5">
                      +6019-4231231
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-50 shrink-0">
                    <i className="ri-mail-line text-teal-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      E-mel
                    </div>
                    <p className="text-xs md:text-sm text-gray-500 mt-0.5">
                      admin@muliainsan.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-50 shrink-0">
                    <i className="ri-time-line text-teal-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      Waktu Operasi
                    </div>
                    <p className="text-xs md:text-sm text-gray-500 mt-0.5">
                      Isnin - Jumaat: 9:00 pagi - 5:00 petang
                      <br />
                      Sabtu: 9:00 pagi - 1:00 tengah hari
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Map embed */}
              <div className="rounded-xl overflow-hidden border border-gray-100 h-56 md:h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3984.1234567890123!2d101.7831!3d2.9932!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMcKwNTknMzUuNSJOIDEwMcKwNDYnNTkuMiJF!5e0!3m2!1sen!2smy!4v1600000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokasi Pejabat PERTUBUHAN MULIA INSAN MALAYSIA"
                  aria-label="Peta Google lokasi pejabat kami di Kajang, Selangor"
                />
              </div>
            </div>

            {/* Contact form */}
            <div>
              <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
                Hantar Mesej
              </p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Borang Hubungan
              </h2>

              {formStatus === "sent" && (
                <div className="mb-5 p-4 rounded-lg bg-primary-50 text-primary-700 text-sm flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-lg" />
                  Terima kasih! Mesej anda telah dihantar. Kami akan menghubungi
                  anda segera.
                </div>
              )}
              {formStatus === "error" && (
                <div className="mb-5 p-4 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line text-lg" />
                  Maaf, terdapat masalah semasa menghantar. Sila cuba lagi.
                </div>
              )}

              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-xs font-medium text-gray-700 mb-1.5"
                    >
                      Nama Penuh
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
                      placeholder="Nama anda"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-medium text-gray-700 mb-1.5"
                    >
                      Alamat E-mel
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
                      placeholder="email@contoh.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-xs font-medium text-gray-700 mb-1.5"
                    >
                      No. Telefon
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
                      placeholder="+6019-4231231"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-xs font-medium text-gray-700 mb-1.5"
                    >
                      Subjek
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors bg-white"
                    >
                      <option value="">Pilih subjek</option>
                      <option value="donation">Sumbangan</option>
                      <option value="volunteer">Sukarelawan</option>
                      <option value="training">Latihan AI & Komputer</option>
                      <option value="aid">Mohon Bantuan</option>
                      <option value="partnership">Kerjasama</option>
                      <option value="other">Lain-lain</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-xs font-medium text-gray-700 mb-1.5"
                  >
                    Mesej
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors resize-none"
                    placeholder="Tulis mesej anda di sini..."
                  />
                  <p className="text-xs text-gray-400 mt-1">Maksimum 500 aksara</p>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === "sending"}
                  className="w-full sm:w-auto px-6 py-3 bg-teal-700 text-white text-sm font-semibold rounded-full hover:bg-teal-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {formStatus === "sending" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> Menghantar...
                    </>
                  ) : (
                    <>
                      Hantar Mesej <i className="ri-send-plane-line" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Social */}
      <section className="py-14 md:py-16 px-4 md:px-8 lg:px-12 bg-earth-50">
        <AnimatedSection className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-xl md:text-2xl font-bold text-gray-900 mb-3">
            Ikuti Kami di Media Sosial
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Dapatkan kemas kini terkini tentang program dan aktiviti kami.
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="#"
              className="w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:border-teal-300 hover:bg-teal-50 transition-all"
              aria-label="Facebook"
              rel="nofollow"
            >
              <i className="ri-facebook-fill text-teal-700" />
            </a>
            <a
              href="#"
              className="w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:border-teal-300 hover:bg-teal-50 transition-all"
              aria-label="Instagram"
              rel="nofollow"
            >
              <i className="ri-instagram-line text-teal-700" />
            </a>
            <a
              href="#"
              className="w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:border-teal-300 hover:bg-teal-50 transition-all"
              aria-label="TikTok"
              rel="nofollow"
            >
              <i className="ri-tiktok-fill text-teal-700" />
            </a>
            <a
              href="#"
              className="w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:border-teal-300 hover:bg-teal-50 transition-all"
              aria-label="YouTube"
              rel="nofollow"
            >
              <i className="ri-youtube-fill text-teal-700" />
            </a>
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
}